import {
  Code2,
  Database,
  Download,
  FolderOpen,
  Loader2,
  PanelLeft,
  PlayCircle,
} from "lucide-react";
import type React from "react";
import { useState } from "react";
import { trackEvent } from "@/features/analytics";
import { renderAndExportVideo } from "@/features/export";
import { useProject } from "@/features/project";
import { useTab } from "@/features/Tab";
import { useUI } from "@/features/ui";

export const Toolbar: React.FC = () => {
  const { viewMode, setViewMode } = useTab();
  const { files, dimensions, duration, assetUrlMap, data } = useProject();
  const { showChat, setShowChat, isExportHighlighted } = useUI();

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const animDuration = duration || 3;

  const handleExport = async () => {
    if (isExporting) return;

    const isMobile =
      /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent));

    if (isMobile) {
      alert(
        "Mobile export is currently not supported. Please use a desktop computer to export your video.",
      );
      return;
    }

    setIsExporting(true);
    setExportProgress(0);
    const exportStartedAt = Date.now();
    trackEvent("export_start", {
      width: dimensions.width,
      height: dimensions.height,
      duration_sec: animDuration,
      fps: 30,
      is_mobile: isMobile,
    });

    try {
      const blob = await renderAndExportVideo(
        files,
        dimensions.width,
        dimensions.height,
        animDuration,
        30,
        setExportProgress,
        assetUrlMap,
        data,
      );
      trackEvent("export_complete", {
        width: dimensions.width,
        height: dimensions.height,
        duration_sec: animDuration,
        fps: 30,
        file_size_bytes: blob.size,
        export_time_ms: Date.now() - exportStartedAt,
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gliakinetics-render-${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Export failed", e);
      trackEvent("export_error", {
        width: dimensions.width,
        height: dimensions.height,
        duration_sec: animDuration,
        fps: 30,
        error_type: e instanceof Error ? e.name : "unknown",
        is_mobile: false, // Mobile should be blocked by now
      });

      const errorMessage = (e as Error).message;
      alert(`Export failed: ${errorMessage}`);
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  return (
    <div className="h-14 flex items-center p-2 sm:p-3 border-b border-slate-800 bg-slate-950 justify-between z-20 relative gap-2 sm:gap-3">
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Expand Chat Button */}
        {!showChat && (
          <button onClick={() => setShowChat(true)} className="text-slate-500 hover:text-slate-300">
            <PanelLeft size={18} />
          </button>
        )}

        {/* View Toggle */}
        <div className="flex items-center bg-slate-900 rounded-lg p-1 border border-slate-800 h-8">
          <button
            onClick={() => setViewMode("preview")}
            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 h-full rounded-md text-xs font-medium transition-all ${
              viewMode === "preview"
                ? "bg-slate-700 text-white shadow-sm"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <PlayCircle size={14} />
            <span className="hidden sm:inline">Preview</span>
          </button>
          <button
            onClick={() => setViewMode("code")}
            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 h-full rounded-md text-xs font-medium transition-all ${
              viewMode === "code"
                ? "bg-slate-700 text-white shadow-sm"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <Code2 size={14} />
            <span className="hidden sm:inline">Code</span>
          </button>
          <button
            onClick={() => setViewMode("assets")}
            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 h-full rounded-md text-xs font-medium transition-all ${
              viewMode === "assets"
                ? "bg-slate-700 text-white shadow-sm"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <FolderOpen size={14} />
            <span className="hidden sm:inline">Assets</span>
          </button>
          {viewMode === "state" && (
            <button
              onClick={() => setViewMode("state")}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 h-full rounded-md text-xs font-medium transition-all bg-purple-900/50 text-purple-200 shadow-sm border border-purple-500/30"
            >
              <Database size={14} />
              <span className="hidden sm:inline">State</span>
            </button>
          )}
        </div>
      </div>

      {/* Right Group */}
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Export Button */}
        <button
          onClick={handleExport}
          disabled={isExporting}
          className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 h-8 bg-indigo-600 hover:bg-indigo-500 text-white rounded-md text-xs font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed border border-indigo-500 shadow-sm shadow-indigo-500/20 ${
            isExportHighlighted ? "ring-4 ring-indigo-400 animate-pulse scale-105 z-50" : ""
          }`}
        >
          {isExporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          <span className="hidden sm:inline">
            {isExporting ? `${Math.round(exportProgress * 100)}%` : "Export MP4"}
          </span>
        </button>
      </div>
    </div>
  );
};
